/* CaseInfoChipRow — universal informational chip row for the case
 * header, mirrored across every portal (Doctor · Admin · Planner ·
 * QA · Production · Compliance · Scanning Technician).
 *
 * iter100.75 — Extracted from the doctor-only implementation so all
 * portals share the same visual language for the informational chip
 * band that sits under the patient identity + status pill on the
 * case detail page. The specific chips shown:
 *
 *   - Case ID              (teal)
 *   - Created <date>       (teal)
 *   - Planning · <mode>    (green)     · CoE / Self-planned
 *   - Manufacturing · <t>  (green)     · Standard / priority + TAT
 *   - IO Scans by Invisique · <state>  (green)
 *   - Compliance · <state> (green|red) · Invisique / Self / Not chosen
 *   - Rev N                (amber)     · only when revisionCount > 0
 *
 * The Compliance chip becomes a clickable red-pulse popover chooser
 * ONLY when the viewer is the doctor AND the case is in a state
 * where the mode can still be picked (not draft/cancelled/completed).
 * All other portals see the same chip as a static rose-tinted state
 * so it's still clear the choice hasn't been made, but they can't
 * mutate it.
 */
import React, { useState, useEffect } from "react";
import {
    Hash, CalendarBlank, PencilSimple, Sparkle, Timer,
    ScanSmiley, ShieldCheck, Stethoscope, ArrowsClockwise,
    CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

/* Uniform chip primitive. Kept small and self-contained so the row
 * can be rendered inside any portal's case-detail header. */
export function CaseChip({ icon: Icon, label, tone = "neutral", testid, titleTip }) {
    const palette = {
        teal:    "text-teal-800 bg-teal-50 border-teal-200",
        green:   "text-emerald-800 bg-emerald-50 border-emerald-200",
        muted:   "text-slate-500 bg-white border-dashed border-slate-300",
        amber:   "text-amber-800 bg-amber-50 border-amber-200",
        primary: "text-primary bg-primary/10 border-primary/25",
        neutral: "text-slate-700 bg-slate-50 border-slate-200",
        mono:    "text-slate-800 bg-slate-100 border-slate-200 font-mono tabular-nums",
        violet:  "text-violet-700 bg-violet-50 border-violet-200",
        rose:    "text-rose-800 bg-rose-50 border-rose-200",
    };
    const cls = palette[tone] || palette.neutral;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border text-[11px] font-semibold ${cls}`}
            data-testid={testid}
            title={titleTip}
        >
            <Icon size={11} weight="fill" />
            {label}
        </span>
    );
}

/* Doctor-only clickable Compliance upgrade chip. Opens a popover with
   the ONE upgrade option ("Invisique™ manages it"), shows the fee,
   confirms with the doctor, then PATCHes /cases/{id}/compliance-mode.
   Rose-tinted + pulsing until upgraded. Self-managed is the implicit
   default — no "revert to self" option, upgrading is one-way (see
   backend PATCH endpoint). */
export function ComplianceChipChooser({ caseId, onChosen }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fee, setFee] = useState(null);

    /* Pull the current admin-configured fee so the doctor sees the
       amount BEFORE they confirm. Fetched lazily on open so we don't
       thrash /settings on every case-list mount. */
    useEffect(() => {
        if (!open || fee !== null) return;
        (async () => {
            try {
                const r = await api.get("/settings/compliance-fee");
                setFee(typeof r?.data?.fee === "number" ? r.data.fee : 0);
            } catch {
                setFee(0);
            }
        })();
    }, [open, fee]);

    const upgrade = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const r = await api.patch(`/cases/${caseId}/compliance-mode`, { mode: "invisique" });
            const charged = r?.data?.fee_charged;
            const invoiceUpdated = r?.data?.invoice_updated;
            toast.success(
                invoiceUpdated
                    ? `Invisique™ will manage compliance · ₹${(charged || 0).toLocaleString("en-IN")} added to invoice`
                    : "Invisique™ will manage compliance on this case"
            );
            setOpen(false);
            if (onChosen) onChosen();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not upgrade compliance mode");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1 px-2 h-6 rounded-full border font-semibold text-[11px] text-rose-800 bg-rose-50 border-rose-300 hover:bg-rose-100 hover:border-rose-400 transition-colors cursor-pointer animate-compliance-pulse"
                    data-testid="case-monitoring-mode-empty"
                    title="Upgrade to Invisique-managed compliance"
                    aria-label="Upgrade to Invisique-managed compliance"
                >
                    <Stethoscope size={11} weight="fill" />
                    Compliance · Upgrade to Invisique-managed
                    <CaretDown size={10} weight="bold" className="opacity-70" />
                </button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="start" className="w-80 p-3 space-y-2">
                <div className="pb-2 border-b border-border/60">
                    <div className="text-[13px] font-semibold">Upgrade to Invisique-managed compliance</div>
                    <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                        You&apos;re currently managing this patient&apos;s compliance yourself. Handing it to Invisique™ enables our CoE-Support team to run check-ins, wear-time reminders and monitoring.
                    </div>
                </div>
                <div className="flex items-start gap-2.5 p-2 rounded-md bg-primary/5 border border-primary/15">
                    <ShieldCheck size={20} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-semibold text-foreground">Invisique™ manages it</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                            Check-ins, reminders, wear-time monitoring — done for you.
                        </div>
                        <div className="mt-1.5 text-[11px] font-semibold">
                            {fee === null && (
                                <span className="text-muted-foreground">Loading fee…</span>
                            )}
                            {fee !== null && fee <= 0 && (
                                <span className="text-emerald-700">Included · no extra charge</span>
                            )}
                            {fee !== null && fee > 0 && (
                                <span className="text-foreground">
                                    ₹{fee.toLocaleString("en-IN")} <span className="text-muted-foreground font-normal">— added to this case&apos;s invoice</span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="text-[10.5px] text-muted-foreground leading-snug px-0.5">
                    One-way upgrade · locks once aligners are fit on patient.
                </div>
                <button
                    type="button"
                    onClick={upgrade}
                    disabled={saving || fee === null}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-md bg-primary text-primary-foreground font-semibold text-[12.5px] hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="case-compliance-upgrade-confirm"
                >
                    {saving ? "Upgrading…" : "Confirm upgrade"}
                </button>
            </PopoverContent>
        </Popover>
    );
}

function _fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}

/**
 * <CaseInfoChipRow>
 *
 * Universal chip band. Give it a case object and it renders every
 * standard informational chip. The Compliance chip becomes an
 * interactive chooser only for the doctor viewer; every other portal
 * sees a static red-pulse "Not chosen" indicator.
 *
 * Props:
 *   caseObj              — the case document (needs id, code, created_at,
 *                          planning_mode, coe_support_compliance, status,
 *                          revision_count, pricing.*)
 *   viewerRole           — "doctor" | "admin" | "planner" | "qa" | "production" |
 *                          "compliance" | "scanning" — controls chooser interactivity
 *   onComplianceChosen   — called after the doctor picks a compliance mode
 *   className            — optional outer class
 *   extraChips           — optional React node injected before Rev chip
 */
/* iter101.51 — Table variant. Same underlying data as the chip band
 * but rendered as a compact 6-cell horizontal table with a small
 * label above each value (CASE ID / CREATED / PLANNING / etc). Kept
 * as an OPT-IN via `variant="table"` so we can preview it on the
 * planner portal without disturbing the chip variant everywhere
 * else. Collapses gracefully to a two-column grid on narrow
 * screens. */
function _CaseInfoTableRow({
    caseObj,
    className = "",
    onComplianceChosen,
    viewerRole,
}) {
    const {
        code: caseCode,
        created_at: createdAt,
        planning_mode: planningMode,
        coe_support_compliance: monitoringMode,
        status,
        revision_count: revisionCount = 0,
        pricing,
    } = caseObj;

    const priorityCode = (pricing && pricing.priority_code) || "standard";
    const priorityLabel = (pricing && pricing.priority_label) || "Standard";
    const priorityTat = pricing && pricing.priority_tat_days;
    const mfgValue = priorityCode === "standard"
        ? `Standard${priorityTat ? ` · ${priorityTat}-day TAT` : ""}`
        : `${priorityLabel}${priorityTat ? ` · ${priorityTat}-day TAT` : ""}`;

    const ioScanningPaymentChoice = pricing && pricing.io_scanning_payment_choice;
    const ioScanningFee = pricing && pricing.scanner_pickup_fee;
    const hasIoScan = Boolean(ioScanningPaymentChoice) || (typeof ioScanningFee === "number" && ioScanningFee > 0)
                       || Boolean(pricing && pricing.scanner_pickup_requested);
    const ioValue = hasIoScan ? "Requested" : "Not requested";

    const complianceUpgraded = monitoringMode === true;
    const LOCKED_STATUSES = ["in_treatment", "treatment_started", "refinement_requested", "completed", "cancelled"];
    const isLocked = LOCKED_STATUSES.includes(status);
    const canPickCompliance = viewerRole === "doctor" && !complianceUpgraded && !isLocked;

    const planningValue = planningMode === "doctor" ? "Self-planned" : "Invisique™ Team";
    const complianceValue = complianceUpgraded ? "Invisique™ manages" : "Self-managed";
    const complianceTone = complianceUpgraded ? "emerald" : (isLocked ? "slate" : "rose");

    const cells = [
        { label: "Case ID", value: caseCode || "—", mono: true, testid: "case-chip-code" },
        { label: "Created", value: _fmtDate(createdAt), testid: "case-chip-created" },
        { label: "Planning", value: planningValue, testid: planningMode === "doctor" ? "case-planning-mode-self" : "case-planning-mode-coe" },
        { label: "Manufacturing", value: mfgValue, testid: "case-chip-manufacturing" },
        { label: "IO Scans", value: ioValue, testid: "case-chip-io-scan" },
        {
            label: "Compliance",
            value: canPickCompliance ? null : complianceValue,
            tone: complianceTone,
            interactiveChooser: canPickCompliance,
            testid: complianceUpgraded ? "case-monitoring-mode-invisique" : "case-monitoring-mode-empty-readonly",
        },
    ];

    const toneClasses = {
        emerald: "text-emerald-800",
        rose: "text-rose-800",
        slate: "text-slate-700",
    };

    return (
        <div
            className={`rounded-xl border border-teal-200/70 bg-teal-50/30 overflow-hidden ${className}`}
            data-testid="case-info-table"
        >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-y divide-teal-200/50 sm:divide-y-0 sm:divide-x sm:divide-teal-200/50">
                {cells.map((cell) => (
                    <div key={cell.label} className="px-3 py-2 min-w-0">
                        <div className="text-[9px] font-bold tracking-[0.14em] uppercase text-teal-800/70 mb-0.5">
                            {cell.label}
                        </div>
                        {cell.interactiveChooser ? (
                            <div className="mt-0.5" data-testid={cell.testid}>
                                <ComplianceChipChooser
                                    caseId={caseObj.id}
                                    onChosen={() => onComplianceChosen && onComplianceChosen()}
                                />
                            </div>
                        ) : (
                            <div
                                className={`text-[10.5px] font-semibold truncate leading-tight ${cell.mono ? "font-mono tabular-nums" : ""} ${toneClasses[cell.tone] || "text-foreground"}`}
                                data-testid={cell.testid}
                                title={cell.value}
                            >
                                {cell.value}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {revisionCount > 0 && (
                <div className="border-t border-teal-200/60 bg-amber-50/50 px-3 py-1.5 flex items-center gap-1.5">
                    <ArrowsClockwise size={11} weight="fill" className="text-amber-700" />
                    <span className="text-[10.5px] font-bold uppercase tracking-widest text-amber-800" data-testid="case-chip-revision">
                        Revision {revisionCount}
                    </span>
                </div>
            )}
        </div>
    );
}

export function CaseInfoChipRow({
    caseObj,
    viewerRole = "doctor",
    onComplianceChosen,
    className = "",
    extraChips = null,
    variant = "chips",  // "chips" (default) | "table"
}) {
    if (!caseObj) return null;
    if (variant === "table") {
        return (
            <_CaseInfoTableRow
                caseObj={caseObj}
                viewerRole={viewerRole}
                onComplianceChosen={onComplianceChosen}
                className={className}
            />
        );
    }
    const {
        id: caseId,
        code: caseCode,
        created_at: createdAt,
        planning_mode: planningMode,
        coe_support_compliance: monitoringMode,
        status,
        revision_count: revisionCount = 0,
        pricing,
    } = caseObj;

    const priorityCode = (pricing && pricing.priority_code) || "standard";
    const priorityLabel = (pricing && pricing.priority_label) || "Standard";
    const priorityTat = pricing && pricing.priority_tat_days;
    const mfgLabel = priorityCode === "standard"
        ? `Manufacturing · Standard${priorityTat ? ` · ${priorityTat}-day TAT` : ""}`
        : `Manufacturing · ${priorityLabel}${priorityTat ? ` · ${priorityTat}-day TAT` : ""}`;

    const ioScanningPaymentChoice = pricing && pricing.io_scanning_payment_choice;
    const ioScanningFee = pricing && pricing.scanner_pickup_fee;
    const hasIoScan = Boolean(ioScanningPaymentChoice) || (typeof ioScanningFee === "number" && ioScanningFee > 0)
                       || Boolean(pricing && pricing.scanner_pickup_requested);
    const ioLabel = hasIoScan ? "IO Scans by Invisique™ · Requested" : "IO Scans by Invisique™ · Not requested";

    const complianceUpgraded = monitoringMode === true;
    // iter100.76 — "Not upgraded" = default self-managed state. Doctor
    // sees a red pulsing "Upgrade" chip and can click through to buy
    // the upgrade; staff portals see a read-only rose indicator.
    // Upgrade is locked once the aligners are fit on the patient
    // (in_treatment) or the case terminates.
    const LOCKED_STATUSES = ["in_treatment", "treatment_started", "refinement_requested", "completed", "cancelled"];
    const isLocked = LOCKED_STATUSES.includes(status);
    const canPickCompliance = viewerRole === "doctor" && !complianceUpgraded && !isLocked;

    return (
        <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
            <CaseChip
                icon={Hash}
                tone="teal"
                label={caseCode || "—"}
                testid="case-chip-code"
            />
            <CaseChip
                icon={CalendarBlank}
                tone="teal"
                label={`Created ${_fmtDate(createdAt)}`}
                testid="case-chip-created"
            />
            {planningMode === "doctor" ? (
                <CaseChip
                    icon={PencilSimple}
                    tone="green"
                    label="Planning · Self-planned"
                    testid="case-planning-mode-self"
                    titleTip="The doctor authored the treatment plan on this case"
                />
            ) : (
                <CaseChip
                    icon={Sparkle}
                    tone="green"
                    label="Planning · Invisique™ Planning Team"
                    testid="case-planning-mode-coe"
                    titleTip="Invisique™ Planning Team authored the treatment plan"
                />
            )}
            <CaseChip
                icon={Timer}
                tone="green"
                label={mfgLabel}
                testid="case-chip-manufacturing"
                titleTip={priorityTat ? `Expected manufacturing turnaround: ${priorityTat} days` : "Manufacturing turnaround by tier"}
            />
            <CaseChip
                icon={ScanSmiley}
                tone="green"
                label={ioLabel}
                testid="case-chip-io-scan"
                titleTip={hasIoScan ? "Invisique will send an IO-scanning partner to the clinic" : "Doctor submits their own PVS impressions or intraoral scans"}
            />
            {monitoringMode === true && (
                <CaseChip
                    icon={ShieldCheck}
                    tone="green"
                    label="Compliance · Invisique™"
                    testid="case-monitoring-mode-invisique"
                    titleTip="Invisique™ runs patient-side compliance & monitoring on this case"
                />
            )}
            {!complianceUpgraded && canPickCompliance && (
                <ComplianceChipChooser
                    caseId={caseId}
                    onChosen={() => onComplianceChosen && onComplianceChosen()}
                />
            )}
            {!complianceUpgraded && !canPickCompliance && (
                <span
                    className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border font-semibold text-[11px] ${
                        isLocked
                            ? "text-slate-700 bg-slate-50 border-slate-200"
                            : "text-rose-800 bg-rose-50 border-rose-300 animate-compliance-pulse"
                    }`}
                    data-testid="case-monitoring-mode-empty-readonly"
                    title={
                        isLocked
                            ? "Doctor is self-managing compliance (locked once treatment began)"
                            : "Doctor is self-managing compliance on this case"
                    }
                >
                    <Stethoscope size={11} weight="fill" />
                    Compliance · Self-managed
                </span>
            )}
            {extraChips}
            {revisionCount > 0 && (
                <CaseChip
                    icon={ArrowsClockwise}
                    tone="amber"
                    label={`Rev ${revisionCount}`}
                    testid="case-chip-revision"
                />
            )}
        </div>
    );
}

export default CaseInfoChipRow;
